/**
 * The room plate (R23) — one still above the prose, in the Oo-Topos
 * arrangement (D89) — its dissolve (R26), and its composited sprites (R32).
 *
 * @remarks
 * Pure and prop-driven: WHICH plate and WHICH sprites are the engine's
 * judgment (`resolveRoomImage` / `resolveRoomSprites`); this component only
 * frames and stacks them. The frame reserves the pack's declared aspect
 * ratio so the prose never shifts, and the SCENE box inside it mirrors that
 * ratio exactly — it is the contained plate's own rectangle, which is what
 * lets sprites register by fraction without measuring anything.
 *
 * The plate is an `<img>` — or, D266, a video in the same box
 * (`scene-plate.tsx`) — never a control (the no-hotspots law, D89 rule
 * 1), and sprites are DECORATIVE layers: empty alt, `aria-hidden` — the
 * plate's caption describes the frame once (D89 rule 6), the prose names the
 * objects, and a screen-reader user must not hear the room three times.
 *
 * R26's one rule generalizes to the whole scene (D99, Calder's condition
 * one): a scene is only ready when EVERY layer is ready, so the incoming
 * plate AND its sprites decode before the dissolve starts, and the scene
 * commits atomically — a base plate that faded in ahead of its kettle would
 * show the room briefly lying about its own contents. Sprite-only changes
 * (the kettle taken; the breakers thrown) arrive and depart with the same
 * fades the plates use — whenever the resolved scene changes, dissolve.
 */

import { useEffect, useMemo, useRef, useState } from 'react';

import { motionSourceOf, spriteSourceOf, usePrefersReducedMotion } from './reduced-motion.js';
import { decodeImage, decodePlate, restingPlateSourceOf, type ScenePlate, ScenePlateComponent } from './scene-plate.js';
import { useArrivingSprites } from './sprite-transitions.js';

/** One resolvable plate: the asset and its authored caption, together. */
export interface RoomImagePlate {
	/** The authored frame-caption (D89 rule 6), verbatim. */
	readonly alt: string;
	/** D263: the still shown in place of `src` to a viewer who asked for reduced motion — the frame a moving plate owes. */
	readonly reducedMotionSrc?: string;
	/** The plate's asset source, resolved to what the shell actually serves. */
	readonly src: string;
}

/** One composited sprite, placed by fraction of the plate frame (R32). */
export interface RoomImageSprite {
	/**
	 * D146: how the sprite's pixels land — `screen` for emitted light (a
	 * projector cannot project black), `opacity` for a value judged against
	 * the plate. Omitted = paint at full value.
	 */
	readonly compositing?: { readonly blend: 'normal' | 'screen'; readonly opacity: number };
	/** The sprite's top-left corner and width as fractions of the plate frame. */
	readonly placement: { readonly width: number; readonly x: number; readonly y: number };
	/**
	 * D182: the still shown in place of `src` to a viewer who asked for
	 * reduced motion — the frame a moving sprite owes (content-standards §3).
	 */
	readonly reducedMotionSrc?: string;
	/** The sprite's stable identity — arrival/departure is diffed by it. */
	readonly spriteId: string;
	/** The sprite's asset source, resolved to what the shell actually serves. */
	readonly src: string;
	/**
	 * D255: the play-once file that brings this sprite in, drawn in its place
	 * when the player watches it replace `fromSpriteId` (see `sprite-transitions.ts`).
	 */
	readonly transition?: { readonly fromSpriteId: string; readonly src: string };
}

/** A sprite as the frame draws it: the file chosen for this viewer, and the file it rests on (D255). */
interface DisplayedSprite extends RoomImageSprite {
	/** What the sprite draws once any arrival is over — the lightbox's file, so opening it never replays one. */
	readonly restingSrc: string;
}

/** The resolved scene: a plate and its stacked sprites, committed together. */
interface RoomScene {
	readonly plate: ScenePlate;
	readonly sprites: readonly DisplayedSprite[];
}

/** Props for {@link RoomImageComponent}. */
export interface RoomImageComponentProps {
	/** The pack's declared plate ratio (`width:height`), when the manifest declares one. */
	readonly aspectRatio?: string;
	/**
	 * The plate the engine resolved — `undefined` for none (a plate-less room,
	 * or imagery toggled off). The component dissolves between values rather
	 * than cutting, so a change here is an instruction, not a remount.
	 */
	readonly plate?: RoomImagePlate;
	/** The visible sprites, in stacking order (R32); ignored without a plate. */
	readonly sprites?: readonly RoomImageSprite[];
	/**
	 * Why the scene changed, when it changes: a room change is a cut and wants
	 * to be quick; an in-room change (a breaker, a toggle, a taken kettle) is
	 * a thing happening to a standing player and can afford to be slower.
	 */
	readonly transitionKind?: 'in-room' | 'room-change';
}

/**
 * The inline style placing one sprite by fractions of the scene box, and
 * compositing it (D146). The two knobs travel as CUSTOM PROPERTIES rather
 * than as `opacity` / `mix-blend-mode` directly, because the arrival and
 * departure fades animate `opacity` with a `both` fill — an inline opacity
 * would lose to the animation's last keyframe and stay lost. The stylesheet
 * reads the variables in the rule and in the keyframes, so a sprite fades
 * TO its declared value: a projected frame screens onto the door and the
 * gas sits at the value its plate wants — declared in the pack, never
 * re-rendered into the asset.
 */
const spriteStyleOf = (sprite: RoomImageSprite) => ({
	left: `${String(sprite.placement.x * 100)}%`,
	...(sprite.compositing === undefined
		? {}
		: {
			['--sprite-blend' as string]: sprite.compositing.blend,
			['--sprite-opacity' as string]: String(sprite.compositing.opacity),
		}),
	top: `${String(sprite.placement.y * 100)}%`,
	width: `${String(sprite.placement.width * 100)}%`,
});

/** Props for {@link RoomImageLightbox}. */
interface RoomImageLightboxProps {
	/** The pack's declared plate ratio, verbatim from the manifest. */
	readonly aspectRatio?: string;
	/** Leave the lightbox (the close button, and Escape). */
	readonly onClose: () => void;
	/** The settled scene to expand — the same layers the frame is showing. */
	readonly scene: RoomScene;
}

/**
 * D121 (Mike): the lightbox — the same resolved scene, expanded to the full
 * browser width. The frame's plate is capped at a fraction of the viewport so
 * the prompt always stays on screen (the R24 ceiling); this overlay is the
 * deliberate exception, entered and left by the player. The scene box keeps
 * the pack's declared ratio, so sprites register by the same fractions they
 * always did — width is the viewport's, height follows, and when the window
 * is too short for that, height caps and width follows instead.
 *
 * @param props - {@link RoomImageLightboxProps}
 * @returns The expanded scene dialog
 */
const RoomImageLightbox = ({ aspectRatio, onClose, scene }: RoomImageLightboxProps) => {
	const closeButtonRef = useRef<HTMLButtonElement>(null);

	useEffect(() => {
		// The dialog's whole keyboard contract, in one listener: Escape leaves,
		// and Tab stays on the one control there is — a micro focus trap that is
		// exactly the size of its content.
		const onKeyDown = (keyboardEvent: KeyboardEvent) => {
			if (keyboardEvent.key === 'Escape') {
				onClose();
			}

			if (keyboardEvent.key === 'Tab') {
				keyboardEvent.preventDefault();
				closeButtonRef.current?.focus();
			}
		};

		closeButtonRef.current?.focus();
		document.addEventListener('keydown', onKeyDown);

		return () => {
			document.removeEventListener('keydown', onKeyDown);
		};
	}, [onClose]);

	// The pack's ratio, as a number, so the scene box can be sized against BOTH
	// viewport axes (imaged packs always declare one — schema-enforced; the
	// fallback simply lets the plate's own aspect govern). Indexed access is
	// checked: an absent or malformed declaration falls through to the fallback.
	const ratioParts = (aspectRatio ?? '').split(':').map(Number);
	const ratioWidth = ratioParts[0];
	const ratioHeight = ratioParts[1];
	const sceneSizeStyle =
		ratioWidth !== undefined && ratioHeight !== undefined && Number.isFinite(ratioWidth) && Number.isFinite(ratioHeight) && ratioHeight > 0
			? {
				aspectRatio: `${String(ratioWidth)} / ${String(ratioHeight)}`,
				width: `min(100vw, calc(100dvh * ${String(ratioWidth / ratioHeight)}))`,
			}
			: { maxHeight: '100dvh', maxWidth: '100vw' };

	return (
		<div aria-label={scene.plate.alt} aria-modal="true" className="room-image-lightbox" role="dialog">
			<div className="room-image-lightbox__scene" style={sceneSizeStyle}>
				<img alt="" aria-hidden="true" className="room-image__plate" src={restingPlateSourceOf(scene.plate)} />
				{scene.sprites.map((sprite) => (
					<img
						alt=""
						aria-hidden="true"
						className="room-image__sprite"
						key={sprite.spriteId}
						src={sprite.restingSrc}
						style={spriteStyleOf(sprite)}
					/>
				))}
				{/* The D116 shield covers the expanded scene too — a bigger picture
				    is a bigger temptation to inspect the layers. */}
				<div aria-hidden="true" className="room-image__shield" />
			</div>
			<button className="room-image-lightbox__close" onClick={onClose} ref={closeButtonRef} type="button">
				{/* D85 addendum: the sigil is chrome — out of the accessible name. */}
				CLOSE<span aria-hidden="true"> ✕</span>
			</button>
		</div>
	);
};

/**
 * Render the room's scene — plate plus sprites — dissolving on change.
 *
 * @param props - {@link RoomImageComponentProps}
 * @returns The framed scene, or `undefined` once nothing is shown or leaving
 */
export const RoomImageComponent = ({ aspectRatio, plate: authoredPlate, sprites: authoredSprites, transitionKind }: RoomImageComponentProps) => {
	// D182, D263: the viewer's plate and sprites — each moving one replaced by its still
	// when the viewer asked for reduced motion. Chosen ONCE, here, so the
	// scene key, the decode list, the frame and the lightbox all read one
	// `src` and none of them knows there was a choice; and a preference that
	// flips mid-session changes the key, so the still arrives by dissolve.
	// D255 rides the same choice: a sprite the player just watched arrive
	// draws its transition, and rests on its own file thereafter.
	const isReducedMotionPreferred = usePrefersReducedMotion();
	const arrivingIds = useArrivingSprites(authoredSprites);
	const sprites = useMemo(
		() =>
			authoredSprites?.map((sprite): DisplayedSprite => ({
				...sprite,
				restingSrc: spriteSourceOf(sprite, isReducedMotionPreferred, false),
				src: spriteSourceOf(sprite, isReducedMotionPreferred, arrivingIds.has(sprite.spriteId)),
			})),
		[arrivingIds, authoredSprites, isReducedMotionPreferred],
	);
	const plate = useMemo(
		() =>
			authoredPlate === undefined
				? undefined
				: {
					alt: authoredPlate.alt,
					src: motionSourceOf(authoredPlate, isReducedMotionPreferred),
					stillSrc: authoredPlate.reducedMotionSrc ?? authoredPlate.src,
				},
		[authoredPlate, isReducedMotionPreferred],
	);
	// What the frame is showing (the settled scene) and what is on its way out.
	// The incoming plate sits in NORMAL FLOW so the frame's layout story is
	// unchanged; only outgoing copies are absolutely stacked.
	const [displayedScene, setDisplayedScene] = useState<RoomScene | undefined>(
		plate === undefined ? undefined : { plate, sprites: sprites ?? []},
	);
	const [departingPlate, setDepartingPlate] = useState<ScenePlate | undefined>(undefined);
	const [departingSprites, setDepartingSprites] = useState<readonly DisplayedSprite[]>([]);
	// D121: the lightbox — presentation-local like everything else here. The
	// expand button keeps a ref so closing can hand focus straight back.
	const [isExpanded, setIsExpanded] = useState(false);
	const expandButtonRef = useRef<HTMLButtonElement>(null);
	// A monotonically bumped token so a slow decode that loses the race to a
	// newer scene change commits nothing (the classic stale-async guard).
	const changeTokenRef = useRef(0);

	const targetSceneKey =
		plate === undefined ? '' : [plate.src, ...(sprites ?? []).map((sprite) => `${sprite.spriteId}:${sprite.src}`)].join('|');
	const displayedSceneKey =
		displayedScene === undefined
			? ''
			: [displayedScene.plate.src, ...displayedScene.sprites.map((sprite) => `${sprite.spriteId}:${sprite.src}`)].join('|');

	useEffect(() => {
		if (targetSceneKey === displayedSceneKey) {
			return;
		}

		const changeToken = (changeTokenRef.current += 1);
		const targetScene: RoomScene | undefined = plate === undefined ? undefined : { plate, sprites: sprites ?? []};
		const commitChange = () => {
			if (changeTokenRef.current !== changeToken) {
				return;
			}

			// The plate departs as a whole-frame copy only when it CHANGED, and
			// then the whole old scene goes with it: its sprites fade out over
			// its plate, on its clock (Mike, 2026-11-01: leaving the Breach, the
			// patch vanished before the room dissolved). Within one plate,
			// sprites depart individually when they left the resolved set.
			const isPlateChanging = displayedScene !== undefined && displayedScene.plate.src !== targetScene?.plate.src;
			// Asked once, where the absence is real: inside the filter below,
			// `targetScene` has already been proved present by the arm that
			// handles its absence, so a `?.` there would be a question with only
			// one possible answer.
			const arriving = targetScene === undefined ? [] : targetScene.sprites;

			setDepartingPlate(isPlateChanging || targetScene === undefined ? displayedScene?.plate : undefined);
			setDepartingSprites(
				isPlateChanging
					? displayedScene.sprites
					: (displayedScene?.sprites ?? []).filter((sprite) => !arriving.some((next) => next.spriteId === sprite.spriteId)),
			);
			setDisplayedScene(targetScene);
		};

		if (targetScene === undefined) {
			// Leaving entirely: nothing to decode, dissolve to the frame's black.
			commitChange();

			return;
		}

		// D99, Calder's condition one, generalized: a scene is only ready when
		// EVERY layer is — the plate and each sprite decode before the dissolve.
		void Promise.all([decodePlate(targetScene.plate),...targetScene.sprites.map(async (sprite) => decodeImage(sprite.src))]).then(
			commitChange,
			commitChange,
		);
	}, [displayedScene, displayedSceneKey, plate, sprites, targetSceneKey]);

	// D121: a scene that leaves entirely takes the lightbox with it — an
	// expansion must never outlive the thing it expanded and pounce on the
	// next scene to arrive.
	useEffect(() => {
		if (displayedScene === undefined) {
			setIsExpanded(false);
		}
	}, [displayedScene]);

	if (displayedScene === undefined && departingPlate === undefined) {
		return undefined;
	}

	const sceneAspectStyle = aspectRatio === undefined ? {} : { aspectRatio: aspectRatio.replace(':', ' / ') };
	/** Native animationend via ref cleanup — the synthetic events are not delivered under the suite's jsdom. */
	const onFadeEnd = (handleEnd: () => void) => (element: HTMLElement | null) => {
		/*
		 * UNREACHABLE under React 19, and kept because the TYPE still demands it.
		 *
		 * A ref callback that RETURNS A CLEANUP (which this one does, below) is
		 * detached by React calling that cleanup — never by re-invoking the ref
		 * with `null`, which is the React 18 protocol this signature still
		 * describes. So the attach call always receives an element and the
		 * detach call never happens here.
		 *
		 * It is not restructured away because the only way to drop the guard is
		 * to lie in the signature (`element: HTMLElement`), and React's own types
		 * say otherwise; the day this component is read by a React 18 renderer,
		 * or the ref stops returning a cleanup, the branch is live again and
		 * silently dropping it would mean an unremoved listener per fade.
		 *
		 * Verified 2026-08-30: arming a throw here leaves the whole player-ui
		 * suite green, including every dissolve and departure case.
		 */
		/* v8 ignore next 3 */
		if (element === null) {
			return undefined;
		}

		element.addEventListener('animationend', handleEnd);

		return () => {
			element.removeEventListener('animationend', handleEnd);
		};
	};

	return (
		<div
			className="room-image"
			style={{
				...sceneAspectStyle,
				['--plate-fade-duration' as string]: transitionKind === 'in-room' ? '700ms' : '280ms',
			}}
		>
			<div className="room-image__scene" style={sceneAspectStyle}>
				{displayedScene === undefined ? undefined : (
					<ScenePlateComponent
						className="room-image__plate room-image__plate--arriving"
						key={displayedScene.plate.src}
						plate={displayedScene.plate}
					/>
				)}
				{departingPlate === undefined ? undefined : (
					<img
						alt=""
						aria-hidden="true"
						className="room-image__plate room-image__plate--departing"
						key={`departing-${departingPlate.src}`}
						ref={onFadeEnd(() => {
							setDepartingPlate(undefined);
						})}
						src={restingPlateSourceOf(departingPlate)}
					/>
				)}
				{/* R32: the sprites, in stacking order — decorative layers over a
				    frame whose caption already speaks for the whole scene. */}
				{(displayedScene?.sprites ?? []).map((sprite) => (
					<img
						alt=""
						aria-hidden="true"
						className="room-image__sprite room-image__sprite--arriving"
						key={sprite.spriteId}
						src={sprite.src}
						style={spriteStyleOf(sprite)}
					/>
				))}
				{departingSprites.map((sprite) => (
					<img
						alt=""
						aria-hidden="true"
						className="room-image__sprite room-image__sprite--departing"
						key={`departing-${sprite.spriteId}`}
						ref={onFadeEnd(() => {
							setDepartingSprites((current) => current.filter((departing) => departing.spriteId !== sprite.spriteId));
						})}
						src={sprite.src}
						style={spriteStyleOf(sprite)}
					/>
				))}
				{/*
				 * D116 (Mike): the composite shield — a transparent layer over the
				 * whole scene that RECEIVES pointer events, so a right-click lands
				 * here rather than on a sprite `<img>` and the context menu never
				 * names the individual layers. Casual deterrence only, stated
				 * honestly: devtools sees everything, and nothing should pretend
				 * otherwise. It deliberately does NOT carry `pointer-events: none`
				 * — an inert overlay would shield nothing, since events would pass
				 * straight through to the layers beneath.
				 */}
				<div aria-hidden="true" className="room-image__shield" />
				{/* D121: the way into the lightbox — above the shield, because a
				    control the shield swallows is no control at all. Rendered only
				    while a scene is actually showing; an empty frame expands to
				    nothing worth seeing. */}
				{displayedScene === undefined ? undefined : (
					<button
						className="room-image__expand"
						onClick={() => {
							setIsExpanded(true);
						}}
						ref={expandButtonRef}
						type="button"
					>
						{/* D85 addendum: the sigil is chrome — out of the accessible name. */}
						EXPAND<span aria-hidden="true"> ⤢</span>
					</button>
				)}
			</div>
			{isExpanded && displayedScene !== undefined ? (
				<RoomImageLightbox
					aspectRatio={aspectRatio}
					onClose={() => {
						setIsExpanded(false);
						expandButtonRef.current?.focus();
					}}
					scene={displayedScene}
				/>
			) : undefined}
		</div>
	);
};
